import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { StripeConstants } from "@lifeat/shared/stripe/StripeConstants";
import { ProSubscriptionType } from "@lifeat/shared/stripe/StripeSubscriptionTypes";
import { getCurrentPrice } from "@lifeat/shared/stripe/StripeSubscriptions";
import { QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { HorizontalDivider } from "@common/LifeAtDivider/LifeAtDivider";
import { UpgradeCtaContextType } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { sendClarityEvent } from "@features/clarity/Clarity";
import { ClarityEvent } from "@features/clarity/ClarityEvents";
import useStripeSession from "@hooks/useStripeSession";
import UserIcon from "@icons/UserIcon.svg";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { AdditionalSourceData, EnterProSubUpgradeFlowEvent } from "@user/AmplitudeProSubsEvents";
import { BrowserUtil } from "@util/index";
import { PricingTierContent } from "../PricingConstants";
import CheckoutButton from "./CheckoutButton";
import FeatureItem from "./FeatureItem";

const PricingTiersContainer = styled.div<{ isHighlighted?: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px;
    background: ${(props) => props.theme.backgroundPrimary};

    ${(props) =>
        props.isHighlighted &&
        `
        border: 2px solid ${Styles.lifeatSpecialBlue};
    `}

    border-radius: ${Styles.borderRadiusNormal};
    gap: 10px;
`;

const PricingTierTitle = styled.div<{ isHighlighted?: boolean }>`
    font-size: ${FontSize.large};
    color: ${(props) => (props.isHighlighted ? Styles.buttonFillBlue : props.theme.textColorPrimary)};
`;

const Price = styled.div`
    display: flex;
    align-items: baseline;
    gap: 7px;

    color: ${(props) => props.theme.textColorPrimary};

    font-size: ${FontSize.large};
    font-weight: bold;
`;

const PriceSubtext = styled.span`
    font-size: ${FontSize.small};
    font-weight: normal;
`;

const StrikeText = styled.span`
    text-decoration: line-through;
`;

const BlueText = styled.span`
    color: ${Styles.buttonFillBlue};
`;

const FeatureListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const StyledUserIcon = styled(UserIcon)`
    fill: white;
`;

const GET_STARTED_BUTTON_LABEL = "Get Started";
const CURRENT_PLAN_BUTTON_LABEL = "Your current plan";
const AUTH_BUTTON_LABEL = "Sign Up or Log In";
const MONTHLY_PRICE_SUBTEXT = "/month";
const ANNUAL_PRICE_SUBTEXT = "/month, billed yearly";
const FAKE_DISCOUNT_RATE = 0.5;

export function FreeTier() {
    const { userData }: UserContextType = useUserContext();

    return (
        <PricingTiersContainer>
            <PricingTierTitle>{PricingTierContent["free"].title}</PricingTierTitle>
            <Price>$0</Price>
            <HorizontalDivider />
            <FeatureListContainer>
                <FeatureItem featureName={"Limited background spaces"} iconType={"check"} />
                <FeatureItem featureName={"Limited widgets"} iconType={"check"} />
                <FeatureItem featureName={"No access to daily planner"} iconType={"cross"} />
                <FeatureItem featureName={"No access to focus results and analytics"} iconType={"cross"} />
            </FeatureListContainer>
            <CheckoutButton isActive={false} isDisabled={true}>
                {userData?.proUserInfo?.isProUser ? GET_STARTED_BUTTON_LABEL : CURRENT_PLAN_BUTTON_LABEL}
            </CheckoutButton>
        </PricingTiersContainer>
    );
}

interface ProTierProps {
    readonly subscriptionPeriod: ProSubscriptionType;
    readonly amplitudeEventSource: UpgradeCtaContextType;
    readonly amplitudeEventAdditionalSourceData?: AdditionalSourceData;
    readonly skipTrial?: boolean;
    readonly onUpgradeCb?: () => Promise<void>;
    readonly overrideButtonText?: string;
    readonly className?: string;
}

export function ProTier(props: ProTierProps) {
    const { user, userData }: UserContextType = useUserContext();
    const [isRedirecting, setIsRedirecting] = useState(false);
    const history = useHistory();
    const amplitudeService = AmplitudeService.getInstance();

    const requireLogin = !user && !userData;
    const isUserCurrentlyPro = userData?.proUserInfo?.isProUser;

    const { getCheckoutUrl } = useStripeSession();

    const handleClickSubscription = async () => {
        setIsRedirecting(true);
        amplitudeService.logEvent(
            new EnterProSubUpgradeFlowEvent({
                accountCreationDate: user.metadata.creationTime,
                subscriptionPeriod: props.subscriptionPeriod,
                eventSource: props.amplitudeEventSource,
                additionalSourceData: props.amplitudeEventAdditionalSourceData,
            }),
        );
        GA4Service.logEvent(GA4Event.STRIPE_CHECKOUT_BUTTON_CLICK);
        sendClarityEvent(ClarityEvent.CHECKOUT_CLICK);
        const sessionUrl = await getCheckoutUrl(props.subscriptionPeriod, props.skipTrial ?? false);
        BrowserUtil.redirectTo(sessionUrl, undefined);
    };

    const disableCheckoutButton = isRedirecting || isUserCurrentlyPro;

    const redirectSuccess = encodeURIComponent(`/${Routes.App}?${QueryParameters.ShowUpgrade}=true`);
    const authRedirectUrl = `/${Routes.DesktopMain}?${QueryParameters.RedirectSuccess}=${redirectSuccess}`;
    const authButton = (
        <CheckoutButton onClick={() => history.push(authRedirectUrl)} isActive={true}>
            <StyledUserIcon /> {AUTH_BUTTON_LABEL}
        </CheckoutButton>
    );

    let checkoutButtonText = GET_STARTED_BUTTON_LABEL;
    if (props.overrideButtonText) {
        checkoutButtonText = props.overrideButtonText;
    } else if (isUserCurrentlyPro) {
        checkoutButtonText = CURRENT_PLAN_BUTTON_LABEL;
    } else if (!props.skipTrial && userData?.proUserInfo == null) {
        checkoutButtonText = `Try ${StripeConstants.FreeTrialDurationDays} days for free`;
    }

    let priceTextComponent: React.ReactNode;
    let subscriptionPrice: number;
    switch (props.subscriptionPeriod) {
        case "monthly":
            subscriptionPrice = getCurrentPrice("monthly");
            break;
        case "annual":
            subscriptionPrice = Math.round((getCurrentPrice("annual") / 12) * 100) / 100;
            break;
    }

    priceTextComponent = (
        <>
            {props.skipTrial && (
                <>
                    <StrikeText>${subscriptionPrice / FAKE_DISCOUNT_RATE}</StrikeText>
                    <BlueText>${subscriptionPrice}</BlueText>
                    <PriceSubtext>
                        {props.subscriptionPeriod === "monthly" && <span>{MONTHLY_PRICE_SUBTEXT}</span>}
                        {props.subscriptionPeriod === "annual" && <span>{ANNUAL_PRICE_SUBTEXT}</span>}
                    </PriceSubtext>
                </>
            )}
            {!props.skipTrial && (
                <>
                    <StrikeText>${subscriptionPrice}</StrikeText>
                    FREE {StripeConstants.FreeTrialDurationDays} day trial
                </>
            )}
        </>
    );

    return (
        <PricingTiersContainer className={props.className} isHighlighted={!isUserCurrentlyPro}>
            <PricingTierTitle isHighlighted={true}>{PricingTierContent["pro"].title}</PricingTierTitle>
            <Price>{priceTextComponent}</Price>
            <HorizontalDivider />
            <FeatureListContainer>
                <FeatureItem
                    featureName={"1,000+ Focus video and image spaces"}
                    iconType={"check"}
                    isHighlighted={!isUserCurrentlyPro}
                />
                <FeatureItem
                    featureName={"Unlimited focus sounds & ambiance"}
                    iconType={"check"}
                    isHighlighted={!isUserCurrentlyPro}
                />
                <FeatureItem
                    featureName={"Online co-working communities"}
                    iconType={"check"}
                    isHighlighted={!isUserCurrentlyPro}
                />
                <FeatureItem
                    featureName={"Widgets (Task, calendar, pomodoro timer, media, breathwork, and more)"}
                    iconType={"check"}
                    isHighlighted={!isUserCurrentlyPro}
                />
                <FeatureItem
                    featureName={"Unlimited tasks and projects"}
                    iconType={"check"}
                    isHighlighted={!isUserCurrentlyPro}
                />
                <FeatureItem
                    featureName={"Multi-calendar and task-to-cal"}
                    iconType={"check"}
                    isHighlighted={!isUserCurrentlyPro}
                />
                <FeatureItem featureName={"Daily planner"} iconType={"check"} isHighlighted={!isUserCurrentlyPro} />
            </FeatureListContainer>
            {requireLogin && authButton}
            {!requireLogin && (
                <CheckoutButton
                    onClick={() => handleClickSubscription()}
                    isActive={!isUserCurrentlyPro}
                    isDisabled={disableCheckoutButton}
                    isLoading={isRedirecting}
                    target={"_parent"}
                >
                    {checkoutButtonText}
                </CheckoutButton>
            )}
        </PricingTiersContainer>
    );
}
